import React from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { createApi } from "../../utils/createApi";
import {
  AccessibleGroup,
  getHighestAccessibleGroup,
  sortGroupsForPicker,
} from "./wireGuardGroupUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  accessibleGroups: AccessibleGroup[];
  onUploadSuccess: (validateOnlyMode: boolean) => Promise<void>;
}

interface BulkUploadResultRow {
  row_number?: number;
  success?: boolean;
  error?: string;
}

interface BulkUploadSummary {
  total?: number;
  success_count?: number;
  failure_count?: number;
  validate_only_mode?: boolean;
}

interface BulkUploadResponse {
  summary?: BulkUploadSummary;
  results?: BulkUploadResultRow[];
  error?: string;
  detail?: string;
}

const buildBulkUploadFailureMessage = (data?: BulkUploadResponse): string | null => {
  if (!data) {
    return null;
  }

  const failureRows =
    data.results?.filter((row) => row.success === false && row.error) ?? [];
  const failureCount = data.summary?.failure_count ?? failureRows.length;
  const totalCount = data.summary?.total ?? data.results?.length ?? 0;

  if (failureCount <= 0 && failureRows.length === 0) {
    return null;
  }

  const previewRows = failureRows.slice(0, 5);
  const rowDetails = previewRows
    .map((row) => `Row ${row.row_number ?? "?"}: ${row.error}`)
    .join(" | ");

  const additionalCount = failureRows.length - previewRows.length;
  const overflowText =
    additionalCount > 0 ? ` | +${additionalCount} more row error(s)` : "";

  return `${failureCount} of ${totalCount} row(s) failed validation/upload.${rowDetails ? ` ${rowDetails}${overflowText}` : ""}`;
};

export const WireGuardBulkUploadDialog = ({
  open,
  onClose,
  accessibleGroups,
  onUploadSuccess,
}: Props) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [validateOnlyMode, setValidateOnlyMode] = React.useState<"false" | "true">(
    "false"
  );
  const [selectedGroup, setSelectedGroup] = React.useState<AccessibleGroup | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const sortedGroups = React.useMemo(
    () => sortGroupsForPicker(accessibleGroups),
    [accessibleGroups]
  );

  // Group is required (ENG-272); default to the top of the user's tree so the
  // common case is one click. Cleared on close, re-applied on the next open.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedGroup((prev) => prev ?? getHighestAccessibleGroup(accessibleGroups));
  }, [open, accessibleGroups]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setSelectedFile(null);
    setValidateOnlyMode("false");
    setSelectedGroup(null);
    setErrorMessage("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedFile || !selectedGroup) {
      return;
    }

    const AxiosUI = createApi("");
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("validate_only_mode", validateOnlyMode);
    formData.append("group_id", String(selectedGroup.id));

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await AxiosUI.post<BulkUploadResponse>(
        "wireguard_tunnel_batch/from-csv/",
        formData
      );

      const failureMessage = buildBulkUploadFailureMessage(response.data);
      if (failureMessage) {
        setErrorMessage(failureMessage);
        const successCount = response.data?.summary?.success_count ?? 0;
        if (validateOnlyMode === "false" && successCount > 0) {
          await onUploadSuccess(false);
        }
        return;
      }

      await onUploadSuccess(validateOnlyMode === "true");
      handleClose();
    } catch (reason) {
      const err = reason as AxiosError<BulkUploadResponse>;
      const structuredFailureMessage = buildBulkUploadFailureMessage(err.response?.data);
      const message =
        structuredFailureMessage ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Bulk upload failed.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="wireguard-bulk-upload-title"
      sx={{ "& *": { fontFamily: "Mulish" } }}
    >
      <DialogTitle
        id="wireguard-bulk-upload-title"
        sx={{ fontFamily: "Mulish", fontWeight: 700, color: "#283E6D" }}
      >
        Bulk Upload WireGuard Tunnels
      </DialogTitle>
      <DialogContent dividers sx={{ px: 4, py: 3 }}>
        <div className="flex flex-col gap-5">
          <FormControl>
            <FormLabel sx={{ fontFamily: "Mulish", color: "#001943", mb: 1 }}>
              File
            </FormLabel>
            <div className="flex items-center gap-3">
              <input
                id="wireguard-bulk-upload-file-input"
                type="file"
                accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setSelectedFile(file);
                }}
                style={{ display: "none" }}
              />
              <label htmlFor="wireguard-bulk-upload-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  sx={{
                    textTransform: "none",
                    width: 128,
                    minWidth: 128,
                    flexShrink: 0,
                  }}
                >
                  Choose File
                </Button>
              </label>
              <Typography
                sx={{
                  fontFamily: "Mulish",
                  fontSize: 14,
                  color: "#5f6368",
                  minWidth: 0,
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-all",
                }}
              >
                {selectedFile ? selectedFile.name : "No chosen file"}
              </Typography>
            </div>
            <FormHelperText sx={{ fontFamily: "Mulish", mt: 1 }}>
              CSV or Excel file with tunnel rows
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel sx={{ fontFamily: "Mulish", color: "#001943", mb: 1 }}>
              Validate Only Mode
            </FormLabel>
            <RadioGroup
              row
              value={validateOnlyMode}
              onChange={(event) =>
                setValidateOnlyMode(event.target.value as "false" | "true")
              }
            >
              <FormControlLabel value="false" control={<Radio />} label="False" />
              <FormControlLabel value="true" control={<Radio />} label="True" />
            </RadioGroup>
            <FormHelperText sx={{ fontFamily: "Mulish" }}>
              If true, validate only; if false, create tunnels.
            </FormHelperText>
          </FormControl>

          <Autocomplete
            options={sortedGroups}
            value={selectedGroup}
            onChange={(_, value) => {
              if (!value) {
                // Keeps the field from going blank once a group is set.
                return;
              }
              setSelectedGroup(value);
            }}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={Boolean(selectedGroup)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Group"
                required
                helperText="*Required — uploaded tunnels are assigned to this group"
                variant="standard"
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
            )}
          />

          {errorMessage && (
            <FormHelperText sx={{ color: "#d32f2f", fontFamily: "Mulish" }}>
              {errorMessage}
            </FormHelperText>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 4 }}>
        <OrangeTextButton onClick={handleClose} text="Cancel" disabled={isSubmitting} />
        <OrangeTextButton
          onClick={handleSubmit}
          text={isSubmitting ? "Uploading..." : "Upload"}
          disabled={isSubmitting || !selectedFile || !selectedGroup}
        />
      </DialogActions>
    </Dialog>
  );
};
